import dayjs from 'dayjs'
import 'dayjs/locale/tr'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)
dayjs.locale('tr')

export interface IDailyReadingTime {
  [date: string]: number // date in YYYY-MM-DD format, value in seconds
}

export interface TimeEntry {
  type: 'day' | 'week'
  label: string
  seconds: number
  dateRange?: string
}

export const RETENTION_DAYS = 90

export const STORAGE_KEYS = {
  DAILY_READING_TIME: 'daily_reading_time',
  TOTAL_READING_TIME: 'total_reading_time',
}

export const formatDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}sa ${minutes}dk`
  }
  if (minutes > 0) {
    return `${minutes}dk ${seconds}sn`
  }
  return `${seconds}sn`
}

export const formatDate = (dateStr: string, currentDate = dayjs()): string => {
  const date = dayjs(dateStr)
  const today = currentDate
  const yesterday = currentDate.subtract(1, 'day')

  if (date.isSame(today, 'day')) {
    return 'Bugün'
  }
  if (date.isSame(yesterday, 'day')) {
    return 'Dün'
  }
  return date.format('dddd, D MMMM')
}

export const cleanupOldData = (
  data: IDailyReadingTime, 
  currentDate = dayjs()
): IDailyReadingTime => {
  const cutoffDate = currentDate.subtract(RETENTION_DAYS, 'day')
  const cleanedData: IDailyReadingTime = {}

  Object.entries(data).forEach(([date, seconds]) => {
    if (dayjs(date).isAfter(cutoffDate)) {
      cleanedData[date] = seconds
    }
  })

  return cleanedData
}

export const groupDataForDisplay = (
  data: IDailyReadingTime, 
  currentDate = dayjs(),
  formatDateFn = formatDate
): TimeEntry[] => {
  const entries: TimeEntry[] = []
  
  // Get the start of current week (Monday)
  const startOfWeek = currentDate.startOf('isoWeek')

  // Sort dates in descending order
  const sortedDates = Object.keys(data).sort(
    (a, b) => dayjs(b).valueOf() - dayjs(a).valueOf(),
  )

  // Group into this week (individual days) and past weeks (weekly summaries)
  const thisWeekDates: string[] = []
  const pastWeekData: {
    [weekKey: string]: {
      dates: string[]
      seconds: number
      startDate: dayjs.Dayjs
      endDate: dayjs.Dayjs
    }
  } = {}

  sortedDates.forEach((date) => {
    const d = dayjs(date)

    if (d.isSame(startOfWeek, 'day') || d.isAfter(startOfWeek)) {
      // This week - show individually
      thisWeekDates.push(date)
    } else {
      // Past weeks - group by week
      const weekStart = d.startOf('isoWeek')
      const weekKey = weekStart.format('YYYY-MM-DD')

      if (!pastWeekData[weekKey]) {
        pastWeekData[weekKey] = {
          dates: [],
          seconds: 0,
          startDate: weekStart,
          endDate: weekStart.endOf('isoWeek'),
        }
      }
      pastWeekData[weekKey].dates.push(date)
      pastWeekData[weekKey].seconds += data[date]
    }
  })

  // Add this week's days
  thisWeekDates.forEach((date) => {
    entries.push({
      type: 'day',
      label: formatDateFn(date, currentDate),
      seconds: data[date],
    })
  })

  // Add past weeks (sorted by most recent first)
  const sortedWeeks = Object.keys(pastWeekData).sort((a, b) => b.localeCompare(a))

  sortedWeeks.forEach((weekKey) => {
    const week = pastWeekData[weekKey]
    const startStr = week.startDate.format('D MMM')
    const endStr = week.endDate.format('D MMM')

    entries.push({
      type: 'week',
      label: `${startStr} - ${endStr}`,
      seconds: week.seconds,
      dateRange: `${week.dates.length} gün`,
    })
  })

  return entries
}
